import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import { 
  Building, ArrowLeft, Save, Trash2, Users, MapPin, Phone, Mail, Globe, 
  CreditCard, Activity, FileText, Settings, RefreshCw, Plus, X,
  TrendingUp, Calendar, DollarSign, Shield, AlertTriangle, CheckCircle,
  Clock, Download, Edit2, UserPlus, Building2, BarChart3, PieChart,
  Briefcase, Factory, Heart, Truck, Zap, Package
} from 'lucide-react';
import PageHeader from './ui/PageHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Industry icons and colors
const INDUSTRY_CONFIG = {
  'Construction': { icon: Building2, color: 'from-orange-500 to-amber-500', bg: 'bg-orange-100', text: 'text-orange-600' },
  'Manufacturing': { icon: Factory, color: 'from-blue-500 to-indigo-500', bg: 'bg-blue-100', text: 'text-blue-600' },
  'Healthcare': { icon: Heart, color: 'from-red-500 to-pink-500', bg: 'bg-red-100', text: 'text-red-600' },
  'Logistics': { icon: Truck, color: 'from-purple-500 to-indigo-500', bg: 'bg-purple-100', text: 'text-purple-600' },
  'Energy': { icon: Zap, color: 'from-yellow-500 to-orange-500', bg: 'bg-yellow-100', text: 'text-yellow-600' },
  'Technology': { icon: Globe, color: 'from-cyan-500 to-blue-500', bg: 'bg-cyan-100', text: 'text-cyan-600' },
  'Retail': { icon: Package, color: 'from-green-500 to-emerald-500', bg: 'bg-green-100', text: 'text-green-600' },
  'General Industry': { icon: Briefcase, color: 'from-gray-500 to-slate-500', bg: 'bg-gray-100', text: 'text-gray-600' },
  'Other': { icon: Building, color: 'from-gray-500 to-slate-500', bg: 'bg-gray-100', text: 'text-gray-600' }
};

const SUBSCRIPTION_STATUS_CONFIG = {
  'active': { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'trialing': { label: 'Trial', color: 'bg-blue-100 text-blue-700', icon: Clock },
  'past_due': { label: 'Past Due', color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
  'canceled': { label: 'Canceled', color: 'bg-red-100 text-red-700', icon: X },
  'unpaid': { label: 'Unpaid', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  'free': { label: 'Free', color: 'bg-gray-100 text-gray-700', icon: Users }
};

const INDUSTRIES = [
  'Construction', 'Manufacturing', 'Healthcare', 'Logistics', 
  'Energy', 'Technology', 'Retail', 'General Industry', 'Other'
];

const CompanyDetailPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [activity, setActivity] = useState(null);
  const [billingHistory, setBillingHistory] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddAdminDialog, setShowAddAdminDialog] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');

  const getToken = () => localStorage.getItem('token');

  const fetchCompany = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/company-management/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setCompany(response.data);
      setEditData(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load company details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, toast]);

  const fetchActivity = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/company-management/companies/${companyId}/activity`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setActivity(response.data);
    } catch (error) {
      // Silently fail for activity
    }
  }, [companyId]);

  const fetchBillingHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/company-management/companies/${companyId}/billing-history`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setBillingHistory(response.data);
    } catch (error) {
      // Silently fail
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      fetchCompany();
      fetchActivity();
      fetchBillingHistory();
    }
  }, [companyId, fetchCompany, fetchActivity, fetchBillingHistory]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/company-management/companies/${companyId}`, editData, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast({ title: "Success", description: "Company updated successfully" });
      setEditMode(false);
      fetchCompany();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update company",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (permanent = false) => {
    try {
      await axios.delete(`${API}/company-management/companies/${companyId}?permanent=${permanent}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast({ 
        title: "Success", 
        description: permanent ? "Company permanently deleted" : "Company deactivated" 
      });
      navigate('/admin/companies');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete company",
        variant: "destructive"
      });
    }
    setShowDeleteDialog(false);
  };

  const getIndustryConfig = (industry) => {
    return INDUSTRY_CONFIG[industry] || INDUSTRY_CONFIG['Other'];
  };

  const getSubscriptionConfig = (status) => {
    return SUBSCRIPTION_STATUS_CONFIG[status] || SUBSCRIPTION_STATUS_CONFIG['free'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-16">
        <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Company not found</h2>
        <Button onClick={() => navigate('/admin/companies')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Companies
        </Button>
      </div>
    );
  }

  const industryConfig = getIndustryConfig(company.industry);
  const IndustryIcon = industryConfig.icon;
  const subscriptionConfig = getSubscriptionConfig(company.billing?.subscription_status);
  const SubscriptionIcon = subscriptionConfig.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/companies')} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${industryConfig.color} flex items-center justify-center shadow-lg`}>
            <IndustryIcon className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={industryConfig.bg + ' ' + industryConfig.text}>
                {company.industry}
              </Badge>
              <Badge className={subscriptionConfig.color}>
                <SubscriptionIcon className="w-3 h-3 mr-1" />
                {subscriptionConfig.label}
              </Badge>
              {!company.is_active && (
                <Badge variant="destructive">Inactive</Badge>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <Button variant="outline" onClick={() => setEditMode(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditMode(true)}>
                <Edit2 className="w-4 h-4 mr-2" /> Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="rounded-xl border-t-4 border-t-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Health Score</p>
                <p className="text-2xl font-bold">{company.health_score}/100</p>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                company.health_score >= 70 ? 'bg-green-100' : company.health_score >= 40 ? 'bg-yellow-100' : 'bg-red-100'
              }`}>
                <TrendingUp className={`w-5 h-5 ${
                  company.health_score >= 70 ? 'text-green-600' : company.health_score >= 40 ? 'text-yellow-600' : 'text-red-600'
                }`} />
              </div>
            </div>
            <Progress value={company.health_score} className="mt-2 h-2" />
          </CardContent>
        </Card>

        <Card className="rounded-xl border-t-4 border-t-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold">${(company.billing?.total_paid || 0).toLocaleString()}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-t-4 border-t-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Employees</p>
                <p className="text-2xl font-bold">{company.employee_count || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-t-4 border-t-orange-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tenure</p>
                <p className="text-2xl font-bold">{company.tenure_days} days</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-t-4 border-t-cyan-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Admin Users</p>
                <p className="text-2xl font-bold">{company.admins?.length || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList className="bg-white border rounded-xl p-1">
          <TabsTrigger value="details" className="rounded-lg">
            <Building className="w-4 h-4 mr-2" /> Details
          </TabsTrigger>
          <TabsTrigger value="billing" className="rounded-lg">
            <CreditCard className="w-4 h-4 mr-2" /> Billing
          </TabsTrigger>
          <TabsTrigger value="usage" className="rounded-lg">
            <BarChart3 className="w-4 h-4 mr-2" /> Usage
          </TabsTrigger>
          <TabsTrigger value="admins" className="rounded-lg">
            <Users className="w-4 h-4 mr-2" /> Admins
          </TabsTrigger>
          <TabsTrigger value="activity" className="rounded-lg">
            <Activity className="w-4 h-4 mr-2" /> Activity
          </TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Company Information */}
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building className="w-5 h-5 mr-2 text-blue-600" />
                  Company Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name</Label>
                    {editMode ? (
                      <Input 
                        value={editData.name || ''} 
                        onChange={(e) => setEditData({...editData, name: e.target.value})}
                      />
                    ) : (
                      <p className="text-gray-900 font-medium">{company.name}</p>
                    )}
                  </div>
                  <div>
                    <Label>Industry</Label>
                    {editMode ? (
                      <Select 
                        value={editData.industry} 
                        onValueChange={(v) => setEditData({...editData, industry: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRIES.map(ind => (
                            <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-gray-900">{company.industry}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Employee Count</Label>
                    {editMode ? (
                      <Input 
                        type="number"
                        value={editData.employee_count || ''} 
                        onChange={(e) => setEditData({...editData, employee_count: parseInt(e.target.value) || 0})}
                      />
                    ) : (
                      <p className="text-gray-900">{company.employee_count || 'Not specified'}</p>
                    )}
                  </div>
                  <div>
                    <Label>Company Size</Label>
                    <p className="text-gray-900 capitalize">{company.company_size || 'Unknown'}</p>
                  </div>
                </div>

                <div>
                  <Label>Website</Label>
                  {editMode ? (
                    <Input 
                      value={editData.website || ''} 
                      onChange={(e) => setEditData({...editData, website: e.target.value})}
                      placeholder="https://..."
                    />
                  ) : (
                    <p className="text-gray-900">
                      {company.website ? (
                        <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {company.website}
                        </a>
                      ) : 'Not specified'}
                    </p>
                  )}
                </div>

                <div>
                  <Label>Notes</Label>
                  {editMode ? (
                    <Textarea 
                      value={editData.notes || ''} 
                      onChange={(e) => setEditData({...editData, notes: e.target.value})}
                      rows={3}
                    />
                  ) : (
                    <p className="text-gray-600">{company.notes || 'No notes'}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Phone className="w-5 h-5 mr-2 text-green-600" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Address</Label>
                  {editMode ? (
                    <Input 
                      value={editData.address || ''} 
                      onChange={(e) => setEditData({...editData, address: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-900">{company.address || 'Not specified'}</p>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    {editMode ? (
                      <Input 
                        value={editData.city || ''} 
                        onChange={(e) => setEditData({...editData, city: e.target.value})}
                      />
                    ) : (
                      <p className="text-gray-900">{company.city || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label>State</Label>
                    {editMode ? (
                      <Input 
                        value={editData.state || ''} 
                        onChange={(e) => setEditData({...editData, state: e.target.value})}
                      />
                    ) : (
                      <p className="text-gray-900">{company.state || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label>ZIP</Label>
                    {editMode ? (
                      <Input 
                        value={editData.zip_code || ''} 
                        onChange={(e) => setEditData({...editData, zip_code: e.target.value})}
                      />
                    ) : (
                      <p className="text-gray-900">{company.zip_code || '-'}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    {editMode ? (
                      <Input 
                        value={editData.phone || ''} 
                        onChange={(e) => setEditData({...editData, phone: e.target.value})}
                      />
                    ) : (
                      <p className="text-gray-900">{company.phone || 'Not specified'}</p>
                    )}
                  </div>
                  <div>
                    <Label>Email</Label>
                    {editMode ? (
                      <Input 
                        type="email"
                        value={editData.email || ''} 
                        onChange={(e) => setEditData({...editData, email: e.target.value})}
                      />
                    ) : (
                      <p className="text-gray-900">{company.email || 'Not specified'}</p>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-base font-semibold">Primary Contact</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <Label className="text-xs text-gray-500">Name</Label>
                      <p className="text-gray-900">{company.primary_contact?.name || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Title</Label>
                      <p className="text-gray-900">{company.primary_contact?.title || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Email</Label>
                      <p className="text-gray-900">{company.primary_contact?.email || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Phone</Label>
                      <p className="text-gray-900">{company.primary_contact?.phone || '-'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CreditCard className="w-5 h-5 mr-2 text-green-600" />
                  Subscription Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="text-sm text-gray-500">Current Plan</p>
                    <p className="text-xl font-bold">{company.billing?.plan_name || 'Free'}</p>
                  </div>
                  <Badge className={subscriptionConfig.color + ' text-lg px-4 py-2'}>
                    {subscriptionConfig.label}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-500">Price</p>
                    <p className="text-xl font-bold">
                      ${company.billing?.plan_price || 0}/{company.billing?.billing_interval || 'month'}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-500">Total Paid</p>
                    <p className="text-xl font-bold text-green-600">
                      ${(company.billing?.total_paid || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                {company.billing?.current_period_end && (
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <p className="text-sm text-blue-600">Current Period Ends</p>
                    <p className="font-medium text-blue-800">
                      {new Date(company.billing.current_period_end).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {company.billing?.trial_end && (
                  <div className="p-4 bg-yellow-50 rounded-xl">
                    <p className="text-sm text-yellow-600">Trial Ends</p>
                    <p className="font-medium text-yellow-800">
                      {new Date(company.billing.trial_end).toLocaleDateString()}
                    </p>
                  </div>
                )}

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-500">Stripe Customer ID</p>
                  <p className="font-mono text-sm">{company.billing?.stripe_customer_id || 'Not linked'}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-purple-600" />
                  Payment History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {billingHistory?.payments?.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {billingHistory.payments.map((payment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">${payment.amount.toFixed(2)}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(payment.date).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className={payment.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {payment.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No payment history</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Usage Tab */}
        <TabsContent value="usage">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="rounded-xl">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Logins</p>
                    <p className="text-2xl font-bold">{company.usage?.total_logins || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Incidents Reported</p>
                    <p className="text-2xl font-bold">{company.usage?.incidents_reported || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Audits Completed</p>
                    <p className="text-2xl font-bold">{company.usage?.audits_completed || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">AI Scans</p>
                    <p className="text-2xl font-bold">{company.usage?.ai_scans_performed || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-2xl mt-6">
            <CardHeader>
              <CardTitle>Most Used Features</CardTitle>
            </CardHeader>
            <CardContent>
              {company.usage?.most_used_features?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {company.usage.most_used_features.map((feature, idx) => (
                    <Badge key={idx} variant="outline" className="px-3 py-1">
                      {feature}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No feature usage data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admins Tab */}
        <TabsContent value="admins">
          <Card className="rounded-2xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2 text-cyan-600" />
                  Company Administrators
                </CardTitle>
                <CardDescription>Users with admin access to this company</CardDescription>
              </div>
              <Button onClick={() => setShowAddAdminDialog(true)}>
                <UserPlus className="w-4 h-4 mr-2" /> Add Admin
              </Button>
            </CardHeader>
            <CardContent>
              {company.admins?.length > 0 ? (
                <div className="space-y-3">
                  {company.admins.map((admin, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                          {admin.name?.[0] || admin.email?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium">{admin.name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">{admin.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant="outline">{admin.role}</Badge>
                        {admin.last_active && (
                          <span className="text-xs text-gray-500">
                            Last active: {new Date(admin.last_active).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No admin users assigned</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle>Recent Incidents</CardTitle>
              </CardHeader>
              <CardContent>
                {activity?.incidents?.length > 0 ? (
                  <div className="space-y-3">
                    {activity.incidents.map((incident, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium">{incident.title || 'Incident'}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(incident.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No recent incidents</p>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle>Recent Audits</CardTitle>
              </CardHeader>
              <CardContent>
                {activity?.audits?.length > 0 ? (
                  <div className="space-y-3">
                    {activity.audits.map((audit, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium">{audit.name || 'Audit'}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(audit.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No recent audits</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Company</DialogTitle>
            <DialogDescription>
              Choose how to remove this company from the system.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Button 
              variant="outline" 
              className="w-full justify-start" 
              onClick={() => handleDelete(false)}
            >
              <X className="w-4 h-4 mr-2" />
              Deactivate (Soft Delete)
              <span className="text-xs text-gray-500 ml-auto">Can be restored</span>
            </Button>
            <Button 
              variant="destructive" 
              className="w-full justify-start" 
              onClick={() => handleDelete(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Permanently Delete
              <span className="text-xs ml-auto">Cannot be undone</span>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Admin Dialog */}
      <Dialog open={showAddAdminDialog} onOpenChange={setShowAddAdminDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Company Admin</DialogTitle>
            <DialogDescription>
              Enter the email of an existing user to grant admin access.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>User Email</Label>
            <Input 
              type="email"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAdminDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              // TODO: Add admin logic
              toast({ title: "Info", description: "Admin invite feature coming soon" });
              setShowAddAdminDialog(false);
            }}>
              Add Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyDetailPage;
